import styled from "styled-components";
import { useState, useRef, useEffect } from "react";
import { gql, useQuery, useLazyQuery, useMutation } from "@apollo/client";
import { captureException } from "@sentry/react";
import { useMediaQuery } from "react-responsive";

import * as css from "styles/CssVariables.js";
import globalConstants from "constants/globalConstants";
import AutoresponderMessageEditor from "./AutoresponderMessageEditor";
import Backdrop from "../other/Backdrop";
import ModalWindow from "../other/ModalWindow";
import ButtonWhite from "../buttons/ButtonWhite";
import ButtonPrimary from "../buttons/ButtonPrimary";
import MessageWindow from "components/other/MessageWindow";

import successIcon from "../../assets/img/icons/icons8-success.svg";

const AutoresponderMessageEditorContainer = styled.div`
  position: relative;

  flex: 1;

  display: flex;
`;

const SavedModal = styled.div`
  margin-top: 50px;
  width: 100%;

  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${css.VERTICAL_BOX_MARGIN_LARGE};
`;

const FormModalWrap = styled.div`
  position: fixed;
  top: 50%;
  left: 50%;
  width: auto;
  transform: translateX(-50%) translateY(-50%);

  color: #fff;

  width: 100%;
  max-width: 520px;
  padding: 0 10px;
  box-sizing: border-box;
  z-index: 200;

  @media (max-width: ${globalConstants.switchMobile}) {
    width: 100%;
  }
`;

const SavedModalInfo = styled.div``;

const SavedModalButtons = styled.div`
  width: 100%;

  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${css.BUTTONS_GAP};
`;

const SavedIcon = styled.div`
  margin-top: 10px;
  margin-bottom: 5px;
  width: 75px;
  height: 75px;

  align-self: center;
`;

export default ({
  messageId,
  autoresponderId,
  closeEditor,
  openTemplatesModal,
  emailEditorRef,
}) => {
  const tabletScreen = useMediaQuery({
    query: `(max-width: ${globalConstants.switchTablet})`,
  });

  const [loadingSaveMessage, setLoadingSaveMessage] = useState(false);
  const [savedModal, setSavedModal] = useState(false);
  const [designLoaded, setDesignLoaded] = useState(() => {
    if (messageId) return false;

    return true;
  });
  const [subject, setSubject] = useState("");
  const [warnings, setWarnings] = useState([]);
  const [errorMessage, setErrorMessage] = useState(null);
  const [mobileOverlay, setMobileOverlay] = useState(() => {
    if (tabletScreen) return true;

    return false;
  });

  const [createAutoresponderModal, setCreateAutoresponderModal] =
    useState(false);

  // Fetch data
  const GET_RESPONDER_MESSAGE_DATA = gql`
    query GetResponderMessageData($messageId: ID!) {
      responderMessageData(messageId: $messageId) {
        id
        subject
        design
        html
      }
    }
  `;

  const [responderMessageData] = useLazyQuery(GET_RESPONDER_MESSAGE_DATA);

  const onEditorReady = async (unlayer) => {
    console.log("Editor READY");

    if (messageId) {
      const { data, error } = await responderMessageData({
        variables: {
          messageId,
        },
        fetchPolicy: "network-only",
      });

      if (error) {
        console.log("error fetching responder message data", error);
        captureException(error);
      }

      console.log("data", data);

      if (data?.responderMessageData.design) {
        unlayer.loadDesign(data.responderMessageData.design);

        setDesignLoaded(true);
      }

      if (data?.responderMessageData.subject) {
        setSubject(data.responderMessageData.subject);
      }
    }
  };

  const SUBMIT_AUTORESPONDER_MESSAGE = gql`
    mutation SubmitAutoresponderMessage(
      $messageId: ID
      $responderId: ID
      $subject: String!
      $design: JSON!
      $html: String!
    ) {
      submitAutoresponderMessage(
        messageId: $messageId
        responderId: $responderId
        subject: $subject
        design: $design
        html: $html
      ) {
        resultCode
      }
    }
  `;

  const [submitAutoresponderMessage] = useMutation(
    SUBMIT_AUTORESPONDER_MESSAGE
  );

  const saveMessage = () => {
    const unlayer = emailEditorRef.current?.editor;

    if (!unlayer) return;

    unlayer?.exportHtml(async (data) => {
      try {
        const { design, html } = data;

        let newWarnings = [];

        if (subject.length === 0) {
          newWarnings.push("subject");
        }

        if (newWarnings.length > 0) {
          setWarnings(newWarnings);
          return;
        }

        setWarnings([]);

        if (!design || !html) {
          // editor not loaded yet
          setLoadingSaveMessage(false);
          return;
        }

        if (messageId && !designLoaded) {
          // design data not loaded yet
          setLoadingSaveMessage(false);
          return;
        }
        console.log("exportHtml", design, html);

        setLoadingSaveMessage(true);

        const result = await submitAutoresponderMessage({
          variables: {
            messageId,
            responderId: autoresponderId,
            subject,
            design,
            html,
          },
        });

        const { resultCode } = result.data.submitAutoresponderMessage;
        console.log({ resultCode });

        setLoadingSaveMessage(false);

        if (resultCode !== 0) {
          setErrorMessage(
            "An error occurred while submitting the request. Please try again or contact support."
          );

          return;
        }

        if (messageId) {
          setSavedModal(true);
        } else {
          closeEditor();
        }
      } catch (error) {
        console.log("saveMessage error", error);
        captureException(error);
        setLoadingSaveMessage(false);
        return;
      }
    });
  };

  // const exportHtml = () => {
  //   const unlayer = emailEditorRef.current?.editor;

  //   unlayer?.exportHtml((data) => {
  //     const { design, html } = data;
  //     console.log("exportHtml", html);
  //   });
  // };

  // handle Saved modal
  const closeSavedModal = () => {
    setSavedModal(false);
  };

  // handle Inputs
  const onSubjectInputChange = (e) => {
    setSubject(e.target.value);
  };

  // handle Error modal
  const closeErrorModal = () => {
    setErrorMessage(null);
  };

  // handle Mobile overlay
  const closeMobileOverlay = () => {
    setMobileOverlay(false);
  };

  return (
    <AutoresponderMessageEditorContainer>
      <AutoresponderMessageEditor
        emailEditorRef={emailEditorRef}
        saveMessage={saveMessage}
        onEditorReady={onEditorReady}
        loadingSaveMessage={loadingSaveMessage}
        subject={subject}
        designLoaded={designLoaded}
        onSubjectInputChange={onSubjectInputChange}
        warnings={warnings}
        openTemplatesModal={openTemplatesModal}
        mobileOverlay={mobileOverlay}
        closeMobileOverlay={closeMobileOverlay}
      />
      {savedModal && (
        <>
          <Backdrop
            onClick={closeSavedModal}
            background="rgba(0, 0, 0, 0.7)"
            insideModal={1}
          />
          <FormModalWrap>
            <ModalWindow
              handleModalClose={closeSavedModal}
              fullScreenHeight={true}
              title="Message Saved Successfully"
            >
              <SavedModal>
                <SavedIcon>
                  <img src={successIcon} style={{ width: "100%" }} />
                </SavedIcon>
                <SavedModalInfo>
                  You can continue editing the design or close the editor.
                </SavedModalInfo>
                <SavedModalButtons>
                  <ButtonWhite onClick={closeSavedModal}>Stay</ButtonWhite>
                  <ButtonPrimary onClick={closeEditor}>
                    Close Editor
                  </ButtonPrimary>
                </SavedModalButtons>
              </SavedModal>
            </ModalWindow>
          </FormModalWrap>
        </>
      )}
      {errorMessage && (
        <>
          <Backdrop
            onClick={closeErrorModal}
            background="rgba(0, 0, 0, 0.7)"
            insideModal={1}
            zIndex="290"
          />
          <MessageWindow handleModalClose={closeErrorModal} type="error">
            {errorMessage}
          </MessageWindow>
        </>
      )}
    </AutoresponderMessageEditorContainer>
  );
};
